<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Results</h1>
        <div>
          <button class="btn btn-outline-secondary mr-2" @click="openUploadModal">
            <b-icon icon="cloud-upload" class="mr-1"></b-icon> Upload Excel
          </button>
          <button class="btn btn-primary" @click="openCreateModal">
            <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Result
          </button>
        </div>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3">
              <b-form-input
                v-model="filters.search"
                placeholder="Matric, email, name, or course code..."
                @keyup.enter="loadResults"
              ></b-form-input>
            </div>
            <div class="col-md-2">
              <b-form-select v-model="filters.session" :options="sessionOptions" @change="loadResults"></b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select v-model="filters.semester" :options="semesterFilterOptions" @change="loadResults"></b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select v-model="filters.remark" :options="remarkFilterOptions" @change="loadResults"></b-form-select>
            </div>
            <div class="col-md-1">
              <button class="btn btn-info btn-block" @click="loadResults" :disabled="loading">
                <b-spinner small v-if="loading"></b-spinner>
                <b-icon icon="search" v-else></b-icon>
              </button>
            </div>
            <div class="col-md-2 text-right">
              <small class="text-muted" v-if="!loading">{{ results.length }} result(s)</small>
            </div>
          </div>

          <b-table
            :items="results"
            :fields="fields"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
            small
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading results...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No results found. Use filters to search.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(student_name)="data">
              {{ data.item.surname }} {{ data.item.first_name }}
            </template>
            <template #cell(grade)="data">
              <strong>{{ data.value }}</strong>
            </template>
            <template #cell(remark)="data">
              <b-badge :variant="data.value === 'PASS' ? 'success' : data.value === 'FAIL' ? 'danger' : 'dark'">{{ data.value }}</b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" :disabled="deleting">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(data.item)" :disabled="deleting">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="results.length > perPage">
            <small class="text-muted">Page {{ currentPage }} of {{ Math.ceil(results.length / perPage) }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="results.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Create/Edit Result Modal -->
      <b-modal
        v-model="showModal"
        :title="isEditing ? 'Edit Result' : 'Add Result'"
        @ok.prevent="saveResult"
        :ok-disabled="saving"
        no-close-on-backdrop
        size="lg"
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>
        <b-form @submit.prevent="saveResult">
          <div v-if="!isEditing">
            <div class="row">
              <div class="col-md-6">
                <b-form-group label="Student (matric or email)" label-for="res-student">
                  <b-input-group>
                    <b-form-input
                      id="res-student"
                      v-model="studentSearch"
                      placeholder="Enter matric no or email"
                      :disabled="saving"
                      @keyup.enter="lookupStudent"
                    ></b-form-input>
                    <b-input-group-append>
                      <button class="btn btn-outline-info" type="button" @click="lookupStudent" :disabled="lookingUp">
                        <b-spinner small v-if="lookingUp"></b-spinner>
                        <span v-else>Find</span>
                      </button>
                    </b-input-group-append>
                  </b-input-group>
                  <small v-if="foundStudent" class="text-success">{{ foundStudent.surname }} {{ foundStudent.first_name }} ({{ foundStudent.matric_no || foundStudent.email }})</small>
                  <small v-if="studentNotFound" class="text-danger">Student not found</small>
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="Course" label-for="res-course">
                  <b-form-select
                    id="res-course"
                    v-model="form.course_id"
                    :options="courseSelectOptions"
                    :disabled="saving"
                  ></b-form-select>
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <b-form-group label="Session" label-for="res-session">
                  <b-form-select
                    id="res-session"
                    v-model="form.session"
                    :options="sessionSelectOptions"
                    :disabled="saving"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="Semester" label-for="res-semester">
                  <b-form-select
                    id="res-semester"
                    v-model="form.semester"
                    :options="[{value:null,text:'Select Semester',disabled:true},{value:1,text:'1st Semester'},{value:2,text:'2nd Semester'}]"
                    :disabled="saving"
                  ></b-form-select>
                </b-form-group>
              </div>
            </div>
          </div>
          <div v-else class="mb-3">
            <strong>Student:</strong> {{ editStudentLabel }} |
            <strong>Course:</strong> {{ editCourseLabel }}
          </div>
          <div class="row">
            <div class="col-md-4">
              <b-form-group label="Score" label-for="res-score">
                <b-form-input id="res-score" v-model="form.score" type="number" min="0" max="100" step="0.5" placeholder="e.g. 65" :disabled="saving"></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Grade" label-for="res-grade">
                <b-form-select id="res-grade" v-model="form.grade" :options="gradeOptions" :disabled="saving"></b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Remark" label-for="res-remark">
                <b-form-select id="res-remark" v-model="form.remark" :options="remarkOptions" :disabled="saving"></b-form-select>
              </b-form-group>
            </div>
          </div>
        </b-form>
      </b-modal>

      <!-- Upload Modal -->
      <b-modal
        v-model="showUploadModal"
        title="Upload Results (Excel)"
        @ok.prevent="uploadResults"
        :ok-disabled="uploading || !uploadFile || !uploadSession || !uploadSemester"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="uploading" class="mr-1"></b-spinner>
          {{ uploading ? 'Uploading...' : 'Upload' }}
        </template>
        <p class="text-muted small mb-3">
          Excel columns: <strong>Matric/Email</strong>, <strong>Course Code</strong>, <strong>Score</strong>, <strong>Grade</strong> (A-F), <strong>Remark</strong> (PASS/FAIL/ABS). First row is header.
        </p>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Session">
              <b-form-select v-model="uploadSession" :options="sessionSelectOptions" :disabled="uploading"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Semester">
              <b-form-select v-model="uploadSemester" :options="[{value:null,text:'Select',disabled:true},{value:1,text:'1st'},{value:2,text:'2nd'}]" :disabled="uploading"></b-form-select>
            </b-form-group>
          </div>
        </div>
        <b-form-group label="Select File">
          <b-form-file v-model="uploadFile" accept=".xlsx,.xls,.csv" placeholder="Choose a file..." :disabled="uploading"></b-form-file>
        </b-form-group>
        <b-progress v-if="uploadProgress > 0" :value="uploadProgress" max="100" show-progress animated class="mt-2"></b-progress>
        <div v-if="uploadFailed.length > 0" class="mt-3">
          <h6 class="text-danger">Failed Rows ({{ uploadFailed.length }})</h6>
          <ul class="small text-danger mb-0">
            <li v-for="(f, i) in uploadFailed" :key="i">Row {{ f.row }}: {{ f.reason }}</li>
          </ul>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResultManagement',
  data() {
    return {
      results: [],
      courses: [],
      settings: [],
      loading: false,
      filters: { search: '', session: null, semester: null, remark: null },
      currentPage: 1,
      perPage: 20,
      fields: [
        { key: 'index', label: '#' },
        { key: 'student_name', label: 'Student' },
        { key: 'matric_no', label: 'Matric' },
        { key: 'course_code', label: 'Course' },
        { key: 'score', label: 'Score' },
        { key: 'grade', label: 'Grade' },
        { key: 'remark', label: 'Remark' },
        { key: 'session', label: 'Session' },
        { key: 'semester', label: 'Sem' },
        { key: 'actions', label: 'Actions' },
      ],
      showModal: false,
      isEditing: false,
      editingId: null,
      editStudentLabel: '',
      editCourseLabel: '',
      saving: false,
      form: { student_id: null, course_id: null, score: null, grade: null, remark: null, session: null, semester: null },
      studentSearch: '',
      foundStudent: null,
      studentNotFound: false,
      lookingUp: false,
      deleting: false,
      deletingId: null,
      showUploadModal: false,
      uploading: false,
      uploadFile: null,
      uploadSession: null,
      uploadSemester: null,
      uploadProgress: 0,
      uploadFailed: [],
      gradeOptions: [
        { value: null, text: 'Select Grade', disabled: true },
        { value: 'A', text: 'A' }, { value: 'B', text: 'B' }, { value: 'C', text: 'C' },
        { value: 'D', text: 'D' }, { value: 'E', text: 'E' }, { value: 'F', text: 'F' },
      ],
      remarkOptions: [
        { value: null, text: 'Select Remark', disabled: true },
        { value: 'PASS', text: 'PASS' }, { value: 'FAIL', text: 'FAIL' }, { value: 'ABS', text: 'ABS (Absent)' },
      ],
    };
  },
  computed: {
    sessionOptions() {
      const sessions = [...new Set(this.settings.map(s => s.session_name))].sort().reverse();
      return [{ value: null, text: 'All Sessions' }, ...sessions.map(s => ({ value: s, text: s }))];
    },
    sessionSelectOptions() {
      const sessions = [...new Set(this.settings.map(s => s.session_name))].sort().reverse();
      return [{ value: null, text: 'Select Session', disabled: true }, ...sessions.map(s => ({ value: s, text: s }))];
    },
    semesterFilterOptions() {
      return [{ value: null, text: 'All Semesters' }, { value: 1, text: '1st' }, { value: 2, text: '2nd' }];
    },
    remarkFilterOptions() {
      return [{ value: null, text: 'All Remarks' }, { value: 'PASS', text: 'PASS' }, { value: 'FAIL', text: 'FAIL' }, { value: 'ABS', text: 'ABS' }];
    },
    courseSelectOptions() {
      return [
        { value: null, text: 'Select Course', disabled: true },
        ...this.courses.map(c => ({ value: c.id, text: c.code + ' - ' + c.description })),
      ];
    },
  },
  created() {
    this.loadSettings();
    this.loadCourses();
  },
  methods: {
    loadSettings() {
      axios.get('/admin/all-settings').then(res => { this.settings = res.data.settings || []; });
    },
    loadCourses() {
      axios.get('/admin/courses').then(res => { this.courses = res.data.courses || []; });
    },
    loadResults() {
      this.loading = true;
      this.currentPage = 1;
      const params = {};
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.session) params.session = this.filters.session;
      if (this.filters.semester) params.semester = this.filters.semester;
      if (this.filters.remark) params.remark = this.filters.remark;

      axios.get('/admin/course-results', { params })
        .then(res => { this.results = res.data.results || []; })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Failed to load results' }); })
        .finally(() => { this.loading = false; });
    },
    lookupStudent() {
      if (!this.studentSearch.trim()) return;
      this.lookingUp = true;
      this.foundStudent = null;
      this.studentNotFound = false;
      axios.get('/admin/students', { params: { search: this.studentSearch.trim() } })
        .then(res => {
          const students = res.data.students || (res.data.data || []);
          if (students.length > 0) {
            this.foundStudent = students[0];
            this.form.student_id = students[0].id;
          } else {
            this.studentNotFound = true;
            this.form.student_id = null;
          }
        })
        .catch(() => { this.studentNotFound = true; })
        .finally(() => { this.lookingUp = false; });
    },
    openCreateModal() {
      this.isEditing = false;
      this.editingId = null;
      this.form = { student_id: null, course_id: null, score: null, grade: null, remark: null, session: null, semester: null };
      this.studentSearch = '';
      this.foundStudent = null;
      this.studentNotFound = false;
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.editStudentLabel = (item.surname || '') + ' ' + (item.first_name || '') + ' (' + (item.matric_no || item.email) + ')';
      this.editCourseLabel = item.course_code + ' - ' + item.course_description;
      this.form = { score: item.score, grade: item.grade, remark: item.remark };
      this.showModal = true;
    },
    saveResult() {
      if (!this.isEditing) {
        if (!this.form.student_id) { Toast.fire({ icon: 'warning', title: 'Find a student first' }); return; }
        if (!this.form.course_id) { Toast.fire({ icon: 'warning', title: 'Select a course' }); return; }
        if (!this.form.session) { Toast.fire({ icon: 'warning', title: 'Select a session' }); return; }
        if (!this.form.semester) { Toast.fire({ icon: 'warning', title: 'Select a semester' }); return; }
      }
      if (!this.form.grade) { Toast.fire({ icon: 'warning', title: 'Select a grade' }); return; }
      if (!this.form.remark) { Toast.fire({ icon: 'warning', title: 'Select a remark' }); return; }

      this.saving = true;
      const request = this.isEditing
        ? axios.put('/admin/course-results/' + this.editingId, this.form)
        : axios.post('/admin/course-results', this.form);

      request
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Saved' });
          this.showModal = false;
          this.loadResults();
        })
        .catch(err => {
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
          Toast.fire({ icon: 'error', title: msg || 'Failed to save' });
        })
        .finally(() => { this.saving = false; });
    },
    confirmDelete(item) {
      Swal.fire({
        title: 'Delete Result?',
        html: `Delete result for <strong>${item.course_code}</strong> - ${item.surname} ${item.first_name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete',
      }).then(result => {
        if (result.isConfirmed) this.deleteResult(item);
      });
    },
    deleteResult(item) {
      this.deleting = true;
      this.deletingId = item.id;
      axios.delete('/admin/course-results/' + item.id)
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Deleted' });
          this.loadResults();
        })
        .catch(err => {
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
          Toast.fire({ icon: 'error', title: msg || 'Failed to delete' });
        })
        .finally(() => { this.deleting = false; this.deletingId = null; });
    },
    openUploadModal() {
      this.uploadFile = null;
      this.uploadSession = null;
      this.uploadSemester = null;
      this.uploadProgress = 0;
      this.uploadFailed = [];
      this.showUploadModal = true;
    },
    uploadResults() {
      if (!this.uploadFile || !this.uploadSession || !this.uploadSemester) return;
      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadFailed = [];

      let formData = new FormData();
      formData.append('file', this.uploadFile);
      formData.append('session', this.uploadSession);
      formData.append('semester', this.uploadSemester);

      axios.post('/admin/course-results/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          this.uploadProgress = parseInt(Math.round((e.loaded / e.total) * 100));
        },
      })
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Uploaded' });
          this.uploadFailed = res.data.failed || [];
          if (this.uploadFailed.length === 0) {
            this.showUploadModal = false;
          }
          this.loadResults();
        })
        .catch(err => {
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
          Toast.fire({ icon: 'error', title: msg || 'Upload failed' });
        })
        .finally(() => { this.uploading = false; });
    },
  },
};
</script>
